import { createAsyncThunk } from "@reduxjs/toolkit";
import { fetchProjectIElements } from "@store/i-elements/i-elements-slice-thunk";
import { generateSdbBackgroundTasks, getTasksIElementIds } from "@utils/sdb-background-tasks-utils";
/**
 * Fetches the list of background tasks from the backend so they can be put into the store
 * Also fetches the iElements related to the tasks if specified
 * @throws {Error} if the Progress API client request fails
 * @throws {ApiResponseError} if the Project API client request fails
 */
export const updateBackgroundTasks = createAsyncThunk("sdbBackgroundTasks/updateBackgroundTasks", async ({
  progressApiClient,
  projectApiClient,
  fromDate,
  pageSize,
  beforePage,
  shouldFetchAllPages = false,
  shouldOverrideBeforePage = true,
  shouldFetchIElements = true,
  taskType
}, {
  dispatch
}) => {
  const {
    data,
    totalCount,
    before,
    after
  } = await progressApiClient.requestProgress({
    fromDate,
    pageSize,
    before: beforePage,
    shouldFetchAllPages,
    taskType
  });
  if (data) {
    const iElementsIds = shouldFetchIElements ? getTasksIElementIds(data) : undefined;

    // If the iElementsIds length is zero then all project iElements will be fetched
    // This is not desired since projects can typically have thousands of iElements and fetching them
    // each minute it's demanding for the SDB and the backend.
    if (iElementsIds?.length) {
      // Fetch IElements associated with the fetched tasks
      await dispatch(fetchProjectIElements({
        fetcher: async () => {
          const elements = await projectApiClient.getAllIElements({
            descendantIds: iElementsIds
          });
          return elements;
        }
      })).unwrap();
    }
    const sdbBackgroundTasks = generateSdbBackgroundTasks(data);
    return {
      sdbBackgroundTasks,
      totalCount,
      before,
      after,
      shouldOverrideBeforePage
    };
  }
  return {
    sdbBackgroundTasks: [],
    totalCount,
    before,
    after,
    shouldOverrideBeforePage
  };
});
/**
 * Fetches tasks of a specific type from the backend so they can be put into the store
 * @throws {Error} if the Progress API client request fails
 */
export const fetchTasksByType = createAsyncThunk("sdbBackgroundTasks/fetchTasksByType", async ({
  progressApiClient,
  taskType
}) => {
  const {
    data,
    totalCount
  } = await progressApiClient.requestProgress({
    before: null,
    taskType
  });
  if (!data) {
    return {
      sdbBackgroundTasks: [],
      totalCount,
      taskType
    };
  }
  return {
    sdbBackgroundTasks: generateSdbBackgroundTasks(data),
    totalCount,
    taskType
  };
});